import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import debounce from "lodash/debounce";
import { useEffect, useMemo } from "react";
import { AuditView } from "../models/AuditView";
import { useUserState } from "@/hooks/userState/useUserState";
import { isBuiltInField } from "@/hooks/views/builtInGridFields";

/** Bumped when the default column set changes; older layouts are ignored once. */
const LAYOUT_VERSION = 9;

const SAVE_DEBOUNCE_MS = 800;

const NO_LAYOUT: { layoutVersion: number; gridState: GridInitialStatePro | null } = {
  layoutVersion: 0,
  gridState: null,
};

/**
 * Persists column visibility, order, widths and pinning per user and view in the user's state.
 * The sort order is not persisted; every visit starts newest day first.
 */
export function useAuditGridState(view: AuditView) {
  const { value, setValue, isReady } = useUserState(`grid:logbookAudit:${view}`, NO_LAYOUT);

  const storedState = value.layoutVersion === LAYOUT_VERSION ? value.gridState : null;

  const saveGridState = useMemo(
    () =>
      debounce((state: GridInitialStatePro) => {
        // Grid-internal columns (the selection checkbox) depend on the viewer's role and are
        // added at render time, so they stay out of the stored layout.
        setValue({
          layoutVersion: LAYOUT_VERSION,
          gridState: {
            columns: {
              ...state.columns,
              orderedFields: state.columns?.orderedFields?.filter(field => !isBuiltInField(field)),
            },
            pinnedColumns: {
              left: state.pinnedColumns?.left?.filter(field => !isBuiltInField(field)),
              right: state.pinnedColumns?.right?.filter(field => !isBuiltInField(field)),
            },
          },
        });
      }, SAVE_DEBOUNCE_MS),
    [setValue],
  );

  useEffect(() => () => saveGridState.flush(), [saveGridState]);

  return { storedState, ready: isReady, saveGridState };
}
